import React, { useRef, useEffect, useState } from 'react';
import { SpinnerGapIcon } from '@phosphor-icons/react';

interface AIPreviewCanvasProps {
  imageUrl: string | null;
  isGenerating: boolean;
  zoom: number;
  panOffset: { x: number; y: number };
  className?: string;
  lightBackground?: boolean;
}

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 1024;

export const AIPreviewCanvas: React.FC<AIPreviewCanvasProps> = ({
  imageUrl,
  isGenerating,
  zoom,
  panOffset,
  className = '',
  lightBackground = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loadError, setLoadError] = useState(false);

  const bgColor = lightBackground ? '#f5f5f5' : '#1E1E20';

  // Draw image when URL changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Clear canvas with background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    if (imageUrl) {
      setLoadError(false);
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        // Scale to fit while maintaining aspect ratio
        const scale = Math.min(CANVAS_WIDTH / img.width, CANVAS_HEIGHT / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = (CANVAS_WIDTH - scaledWidth) / 2;
        const y = (CANVAS_HEIGHT - scaledHeight) / 2;

        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      };

      img.onerror = () => {
        setLoadError(true);
      };

      img.src = imageUrl;
    }
  }, [imageUrl, bgColor]);

  return (
    <div className={`relative overflow-hidden ${lightBackground ? 'bg-neutral-100' : 'bg-carbon'} ${className}`} style={{ minHeight: 0 }}>
      {/* Canvas container with zoom/pan (synced with left) */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
          transformOrigin: 'center center',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        />
      </div>

      {/* Loading indicator - subtle, no overlay */}
      {isGenerating && (
        <div className="absolute top-3 right-3 z-10">
          <SpinnerGapIcon className="w-5 h-5 text-slate-blue animate-spin" />
        </div>
      )}

      {/* Error state */}
      {loadError && !isGenerating && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-10">
          <span className="text-sm text-red-400">Failed to load image</span>
        </div>
      )}

      {/* Empty state */}
      {!imageUrl && !isGenerating && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-center px-8">
            <div className={`w-14 h-14 mx-auto mb-3 rounded-full ${lightBackground ? 'bg-zinc-200' : 'bg-graphite'} flex items-center justify-center`}>
              <SpinnerGapIcon className={`w-7 h-7 ${lightBackground ? 'text-zinc-400' : 'text-zinc-600'}`} />
            </div>
            <p className={`text-sm ${lightBackground ? 'text-zinc-500' : 'text-zinc-500'}`}>
              AI preview will appear here
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPreviewCanvas;
